import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

function formatCardNumber(value) {
  return value
    .replace(/[^0-9]/g, "")
    .slice(0, 16)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

// Left here for potential future brand-specific UI if needed
// function detectBrand(num) {
//   const n = num.replace(/\s/g, "");
//   if (/^4/.test(n)) return "visa";
//   if (/^5[1-5]/.test(n)) return "mastercard";
//   if (/^3[47]/.test(n)) return "amex";
//   return "generic";
// }

export default function CreditCardForm({ onSubmit, insuranceProviderSlug = null }) {
  const [cardNumber, setCardNumber] = useState("");
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [saveInfo, setSaveInfo] = useState(true);
  const [focused, setFocused] = useState("number");
  useEffect(()=>{
    window.scrollTo(0,0)
  },[])

  const isValidNumber = useMemo(() => cardNumber.replace(/\s/g, "").length === 16, [cardNumber]);
  const isValidExpiry = useMemo(() => /^(0[1-9]|1[0-2])\/(\d{2})$/.test(expiry), [expiry]);
  const isValidCvc = useMemo(() => /^\d{3}$/.test(cvc), [cvc]);
  const isValidName = useMemo(() => name.trim().length >= 2, [name]);

  const ready = isValidNumber && isValidExpiry && isValidCvc && isValidName;

  return (
    <div className="space-y-4">
      {/* Single flipping card beside the inputs */}
      <div className="grid grid-cols-1 gap-6 items-start lg:grid-cols-2">
        {/* Flipping Card */}
        <div className="relative w-full" style={{ perspective: '1200px', WebkitPerspective: '1200px' }}>
          <motion.div
            className="overflow-hidden relative w-full h-64 text-white rounded-2xl shadow-2xl"
            whileHover={{ rotateX: 1.5, rotateZ: -0.5 }}
            style={{ background: "linear-gradient(135deg,#0ea5e9,#10b981)", transformStyle: 'preserve-3d' }}
          >
            {/* FRONT */}
            <motion.div
              className="absolute inset-0 px-5 py-4"
              style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', zIndex: focused === 'cvc' ? 0 : 2 }}
              animate={{ rotateY: focused === 'cvc' ? 180 : 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              {/* Watermark */}
              <div className="flex absolute inset-0 justify-center items-center select-none">
                <span className="text-6xl font-black tracking-widest uppercase text-white/10">TripSecure</span>
              </div>
              <div className="relative z-10">
                <div className="flex justify-between items-center">
                  <span className="text-sm tracking-widest uppercase opacity-80">TripSecure</span>
                </div>
                {/* Chip */}
                <div
                  className="mt-5 w-14 h-10 rounded-md"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,223,128,0.95), rgba(219,184,96,0.95))",
                    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.15)",
                  }}
                />
                <div className="mt-4 text-2xl tracking-widest">
                  {cardNumber || "#### #### #### ####"}
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6 text-xs">
                  <div>
                    <div className="opacity-70">Card Holder</div>
                    <div className="mt-1 text-sm font-semibold truncate">{name || "YOUR NAME"}</div>
                  </div>
                  <div>
                    <div className="opacity-70">Expires</div>
                    <div className="mt-1 text-sm font-semibold">{expiry || "MM/YY"}</div>
                  </div>
                </div>
              </div>
            </motion.div>
            {/* BACK */}
            <motion.div
              className="absolute inset-0 px-5 py-4"
              style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', zIndex: focused === 'cvc' ? 2 : 0 }}
              animate={{ rotateY: focused === 'cvc' ? 0 : -180 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              {/* Watermark */}
              <div className="flex absolute inset-0 justify-center items-center select-none">
                <span className="text-6xl font-black tracking-widest uppercase text-white/10">TripSecure</span>
              </div>
              <div className="relative z-10">
                {/* Magnetic stripe */}
                <div className="mt-6 w-full h-10 rounded bg-black/60" />
                {/* Signature + CVC */}
                <div className="flex gap-3 items-center mt-6">
                  <div className="flex-1 h-8 rounded bg-white/90" />
                  <div className="px-3 py-2 w-24 text-sm font-semibold text-center text-black rounded bg-white/90">
                    {cvc || "CVC"}
                  </div>
                </div>
                
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="block mb-1 text-sm font-semibold text-gray-800">Card number</label>
            <input
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              onFocus={() => setFocused("number")}
              placeholder="1234 5678 9012 3456"
              inputMode="numeric"
              className={`w-full px-4 py-3 rounded-xl border ${isValidNumber ? "border-emerald-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-100`}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-800">Name on card</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => setFocused("name")}
                placeholder="Your name"
                className={`w-full px-4 py-3 rounded-xl border ${isValidName ? "border-emerald-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-100`}
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-800">Expiry (MM/YY)</label>
              <input
                value={expiry}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                  if (v.length >= 3) v = v.slice(0, 2) + "/" + v.slice(2);
                  setExpiry(v);
                }}
                onFocus={() => setFocused("expiry")}
                placeholder="MM/YY"
                inputMode="numeric"
                className={`w-full px-4 py-3 rounded-xl border ${isValidExpiry ? "border-emerald-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-100`}
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-800">CVC</label>
              <input
                value={cvc}
                onChange={(e) => setCvc(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                onFocus={() => setFocused("cvc")}
                placeholder="CVC"
                inputMode="numeric"
                className={`w-full px-4 py-3 rounded-xl border ${isValidCvc ? "border-emerald-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-100`}
              />
            </div>
          </div>
          <label className="inline-flex gap-2 items-center select-none">
            <input type="checkbox" checked={saveInfo} onChange={(e) => setSaveInfo(e.target.checked)} className="rounded" />
            <span className="text-sm text-gray-700">Save payment info for future payments</span>
          </label>
          <button
            onClick={() => onSubmit?.({ method: "card", cardNumber, name, expiry, cvc, saveInfo, insuranceProviderSlug })}
            disabled={!ready}
            className="px-4 py-3 w-full font-semibold text-white bg-emerald-600 rounded-xl shadow hover:bg-emerald-700 disabled:opacity-50"
          >
            Pay with Card
          </button>
        </div>
      </div>
    </div>
  );
}


